import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common'
import { AfterContentInit, Component, ViewChild } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { parseISO } from 'date-fns'
import { BehaviorSubject, from, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { catchError, concatMap, delay, filter, map, take, takeUntil, tap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import {
    AssetType,
    Booking,
    BookingCheckoutRequest,
    BookingConfirmationFlow,
    Commission,
    CreateBookingRequest,
    Criteria,
    Fees,
    GuestCount,
    GuestRestrictions,
    Listing,
    ListingBookingCheckoutGQL,
    ListingBookingGQL,
    ListingCancelCheckoutSessionGQL,
    ListingCreateBookingGQL,
    ListingGQL,
    ListingHotelQuery,
    ListingPropertyQuery,
    Psp,
    Role
} from '../../../graphql'
import { defaultGuestRestrictions, dialogWidth, searchListingsCriteriaKey } from '../../constant'
import { Amplitude, Auth, RoleManager } from '../../injectable'
import { BookingSummaryComponent } from '../../module/shared-member/organism/booking-summary/booking-summary'
import { HotelSuiteThumbnailData } from '../../module/shared-member/organism/listing/listing-hotel/listing-hotel'
import {
    ListingTravelDatesComponent,
    SelectedTravel
} from '../../module/shared-member/organism/listing/listing-travel-dates/listing-travel-dates'
import { ListingLink } from '../../module/shared-member/organism/listing/shared/listing-link'
import { TravelDates } from '../../module/shared-member/organism/listing/shared/travel-dates'
import { RequestToBookSummaryComponent } from '../../module/shared-member/organism/request-to-book-summary/request-to-book-summary'
import { searchCriteriaFromStorage, toLocalDate, UnsubscribeOnDestroy, scrollToTop } from '../../utility'
import { ConsumerViewService } from '../../injectable/consumer-view-service'
import { WhiteLabelService } from '../account/white-label-settings/white-label.service'
import { ListingViewTracker } from './listing-view-tracker'

@Component({
    selector: 'app-listing',
    styleUrls: ['listing.scss'],
    templateUrl: 'listing.html',
    providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }]
})
export class ListingComponent extends UnsubscribeOnDestroy implements AfterContentInit {

    viewTracker = new ListingViewTracker();

    AssetType = AssetType

    authenticated: boolean
    isTravelAgent: boolean
    listing: Listing | undefined
    listingId: string | null
    travelDates$: ReplaySubject<TravelDates>
    travelDatesValid$: Observable<boolean>
    travelDates: TravelDates | undefined
    availableFromDate: string | null
    minNightsStay: number
    totalNights: number | undefined
    totalGuests: number
    maxGuests = 100
    maxGuestsExceeded = false
    openBookingPanel = false
    selectedSuite: ListingHotelQuery['hotelListing']['suites'][0]
    businessCustomerReference: string
    bookingSummaryDialogRef: MatDialogRef<BookingSummaryComponent>
    requestToBookSummaryDialogRef: MatDialogRef<RequestToBookSummaryComponent>
    expandMobileFooter = false
    bestRate: string
    listingHasCommission = false
    hotelSuitesThumbnailData: HotelSuiteThumbnailData[]
    consumerView = false
    listingLoaded = false
    isWhiteLabel: boolean;

    // Default fees and commission
    fees = {
        bookingFee: {
            amountMicros: 2_000_000_000,
            currency: 'USD'
        },
    } as Fees

    commission = {
        rate: 107
    } as Commission


    formGroup = new FormGroup({
        guests: new FormControl(this.defaultGuests()),
        businessCustomerReference: new FormControl('', Validators.required)
    })

    private guestsSubject = new Subject<Array<GuestCount>>()
    guestsChanged$ = this.guestsSubject.asObservable()

    private guestRestrictionsSubject: Subject<GuestRestrictions>
    guestRestrictionsChanged$: Observable<GuestRestrictions>

    @ViewChild('travelDatesComponent') travelDatesComponent: ListingTravelDatesComponent
    private guestCounts: Array<GuestCount>

    constructor(
        public _amplitude: Amplitude,
        private _activatedRoute: ActivatedRoute,
        private _auth: Auth,
        private _dialog: MatDialog,
        private _listingGQL: ListingGQL,
        private _bookingGQL: ListingBookingGQL,
        private _createBookingGQL: ListingCreateBookingGQL,
        private _checkoutGQL: ListingBookingCheckoutGQL,
        private _cancelCheckoutSessionGQL: ListingCancelCheckoutSessionGQL,
        private _location: Location,
        private _roleManager: RoleManager,
        private _snackBar: MatSnackBar,
        private _title: Title,
        private _consumerViewService: ConsumerViewService,
        private _whiteLabelService: WhiteLabelService
    ) {
        super()
        this.guestRestrictionsSubject = new BehaviorSubject<GuestRestrictions>(defaultGuestRestrictions)
        this.guestRestrictionsChanged$ = this.guestRestrictionsSubject.asObservable()

        this.isWhiteLabel = this._whiteLabelService.isWhiteLabelEnabled()
        this._consumerViewService.consumerViewSubject.subscribe(consumerView => {
            this.consumerView = consumerView || this.isWhiteLabel
        })

        _activatedRoute.queryParamMap
            .pipe(
                takeUntil(this.destroy$),
                filter(paramMap => paramMap.has('session_id')),
                take(1)
            )
            .subscribe(queryParamsMap => {
                const sessionId = queryParamsMap.get('session_id')
                if (sessionId) {
                    this._cancelCheckoutSessionGQL
                        .mutate({ input: { sessionId } })
                        .pipe(this.takeExactlyOne())
                        .subscribe(async _ => {
                            // Unblocking the blocked availability takes a few millis. So, we wait a bit before reloading the availability
                            // Alternatively, it may be nicer to user the session_id to load the corresponding
                            // booking + dates + guests and not reload the availability. In some sense, the Stripe session_id represents
                            // a hold on the availability and shouldn't trigger a reload.
                            //
                            // TODO: This triggers a pretty ugly refresh. Not sure what the alternative could be.
                            setTimeout(() => window.location.reload(), 300)
                        })
                }
            })


        this._title.setTitle('Listing')

        this._subscriptions.push(this._auth.authenticatedSubject.subscribe(authenticated => {
            this.authenticated = authenticated

            this._roleManager.rolesSubject.subscribe(roles => {
                if (!roles) { return }
                this.isTravelAgent = this._roleManager.hasRole(Role.TravelAgent)
            })
        }))

        this._subscriptions.push(this._activatedRoute.paramMap.subscribe(paramMap => {
            // Child components can trigger a URL change. Making sure we reset state and reload listing
            this.listing = undefined
            this.travelDates = undefined
            this.travelDates$ = new ReplaySubject<TravelDates>()
            this.listingId = paramMap.get('listingId')

            const checkin = paramMap.get('checkin')
            const checkout = paramMap.get('checkout')

            if (checkin && checkout) {
                this.emit(new TravelDates(parseISO(checkin), parseISO(checkout)))
            } else {
                this.emit(new TravelDates(undefined, undefined))
            }
        }))
    }

    ngAfterContentInit(): void {
        const sub = this.formGroup.valueChanges.subscribe((event) => {
            this.businessCustomerReference = event.businessCustomerReference
            const storedCriteria = searchCriteriaFromStorage()
            storedCriteria.guestCriterion = this.formGroup.value.guests
            sessionStorage.setItem(searchListingsCriteriaKey, JSON.stringify(storedCriteria))
        })

        this.travelDatesValid$ = this.travelDates$
            .asObservable()
            .pipe(map(td => !!(td.checkin && td.checkout)))

        this._subscriptions.push(sub)
    }

    defaultGuests(): any {
        const criteria = searchCriteriaFromStorage()
        if (criteria.guestCriterion) { return criteria.guestCriterion }

        return {
            adults: 1,
            children: 0,
            infants: 0
        }
    }

    updateTotalGuests(guests: number) {
        this.totalGuests = guests
    }

    emit(travelDates: TravelDates) {
        this.travelDates = travelDates
        this.totalNights = travelDates?.nights

        setTimeout(() => {
            // TODO: Use behavior subject to pass criteria around? So that only one component knows about session storage
            const criteriaRaw: string | null = sessionStorage.getItem(searchListingsCriteriaKey)

            if (criteriaRaw) {
                const criteria: Criteria = JSON.parse(criteriaRaw)
                sessionStorage.setItem(searchListingsCriteriaKey, JSON.stringify(criteria))
            }

            if (this.listingId) {
                const listingLink = new ListingLink(this.listingId, travelDates)
                this._location.replaceState(listingLink.toPath())
            }

            if (!this.listingLoaded) {
                this.requestListing()
            }

            this.travelDates$.next(travelDates)
        }, 1)
    }

    openBookingConfirmation(selectedTravel: SelectedTravel) {
        const modalData =  {
            listingId: this.listingId,
            travelDates: this.travelDates,
            listingName: this.listing?.summary?.title,
            totalGuests: this.totalGuests,
            adjustedPrice: selectedTravel.adjustedPrice,
            price: selectedTravel.price,
            priceInListingCurrency: selectedTravel.priceInListingCurrency,
            commissionRate: selectedTravel.commisionRate,
            commissionAmount$: selectedTravel.commissionAmount,
            ZARListing: selectedTravel.adjustedPrice.currency === 'ZAR',
            installmentSchedule: this.listing?.installmentSchedule,
            consumerView: this.consumerView
        }

        if (selectedTravel.requestToBook) {
            this.requestToBookSummaryDialogRef = this._dialog.open(RequestToBookSummaryComponent, {
                data: modalData,
                width: dialogWidth.lg,
                maxWidth: '90vw',
                height: '70%',
                maxHeight: '100vh'
            })
            this.requestToBookSummaryDialogRef.componentInstance.$requestToBookConfirmed.subscribe(_ => {
                this.startBookingFlow(selectedTravel)
            })
        } else {
            this.bookingSummaryDialogRef = this._dialog.open(BookingSummaryComponent, {
                data: modalData,
                width: dialogWidth.lg,
                maxWidth: '90vw',
                height: '70%',
                maxHeight: '100vh'
            })

            this.bookingSummaryDialogRef.componentInstance.$proceedToCheckout.subscribe(_ => {
                this.startBookingFlow(selectedTravel)
            })
        }
    }

    async startBookingFlow(selectedTravel: SelectedTravel) {
        const travelDates = selectedTravel.travelDates
        if (!travelDates?.checkin || !travelDates?.checkout) {
            return
        }
        if (!this.formGroup.valid) {
            return
        }

        // If authenticated, start the booking flow. Else, trigger the enquiry popup and capture the info
        this._amplitude.logEvent('Booking Checkout Clicked', this.toEventProperties(travelDates))

        if (selectedTravel.requestToBook) {
            this.createBooking$(selectedTravel)
                .pipe(
                    takeUntil(this.destroy$),
                    delay(500), // Eventually consistent
                    concatMap(bookingId => this.booking$(bookingId)),
                    tap(() => {
                        this.requestToBookSummaryDialogRef?.componentInstance.success()
                    }),
                    catchError(err => {
                        this._snackBar.open(err.message, 'OK', {
                            duration: undefined
                        })
                        this.requestToBookSummaryDialogRef?.componentInstance.fail()
                        throw err
                    })
                )
                .subscribe()
        } else {
            this.createBooking$(selectedTravel)
                .pipe(
                    takeUntil(this.destroy$),
                    delay(500), // Eventually consistent
                    concatMap(bookingId => this.booking$(bookingId)),
                    concatMap(booking => this.checkout$(booking)),
                    concatMap(sessionId => this.stripeCheckout$(sessionId)),
                    catchError(err => {
                        this._snackBar.open(err.message, 'OK', {
                            duration: undefined
                        })
                        this.bookingSummaryDialogRef?.componentInstance.setLoadingState(false)
                        throw err
                    })
                )
                .subscribe()
        }
    }

    setAvailableFromDate($event: string) {
        this.availableFromDate =
            $event ?
                new Intl.DateTimeFormat('en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                }).format(new Date($event))
                : null
    }

    openCalendar() {
        // this is a little ugly but the simplest way to deal with dynamic child views
        // You have use timeout to break out of the current $apply() cycle
        setTimeout(() => {
            const calendarInput: HTMLElement = document.getElementById('listing-travel-dates-checkin-date') as HTMLElement

            if (calendarInput) {
                calendarInput.click()
            }
        }, 0)
    }

    openMobileBookingPanel(value: boolean, event: Event) {
        event.stopPropagation()
        this.openBookingPanel = value
    }

    updateSelectedSuite(selectedSuite: ListingHotelQuery['hotelListing']['suites'][0]) {
        this.maxGuests = selectedSuite?.guestRestrictions?.maxOccupancy
        this.selectedSuite = selectedSuite
        this.listingId = selectedSuite?.hotelSuiteId
        if (!selectedSuite) {return}
        this.guestRestrictionsSubject.next(selectedSuite?.guestRestrictions)
    }

    updateBestRate($event: string) {
        this.bestRate = $event
    }

    updateListingHasCommission($event: boolean) {
        this.listingHasCommission = $event
    }

    updateHotelSuitesThumbnails($event: HotelSuiteThumbnailData[]) {
        this.hotelSuitesThumbnailData = $event
    }

    suiteThumbnailSelect(index: number) {
        const tabIndex = index + 2
        const tabEl: HTMLElement = document.querySelectorAll(`[data-tab-button-index='${tabIndex}']`)[0] as HTMLElement
        this.triggerTabClick(tabEl)
    }

    triggerTabClick(tabEl: HTMLElement) {
        if (tabEl) {
            tabEl.click()
            tabEl.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest'
            })

            scrollToTop()
        }
    }

    handleGuestChanged($event: Array<GuestCount>) {
        this.guestCounts = $event
        this.guestsSubject.next($event)
    }

    propertyLoaded($event: ListingPropertyQuery['property']) {
        if ($event?.guestRestrictions?.maxOccupancy) {
            this.maxGuests = $event?.guestRestrictions?.maxOccupancy
        }
        this.guestRestrictionsSubject.next($event?.guestRestrictions)
    }

    handleMaxGuestsExceeded($event: boolean) {
        this.maxGuestsExceeded = $event
    }

    setBestRate($event: Fees) {
        this.fees = $event
    }

    private toEventProperties(travelDates: TravelDates): any {
        const listing = this.listing

        return {
            listingId: listing?.listingId,
            operatorId: listing?.operator?.operatorId,
            operatorName: listing?.operator?.name,
            checkin: travelDates?.checkin ?? listing?.checkin,
            checkout: travelDates?.checkout ?? listing?.checkout,
            bookingFees: listing?.fees?.bookingFee?.amountMicros / 1_000_000,
        }
    }

    private requestListing() {
        if (this.listingId) {
            this._listingGQL.fetch({ listingId: this.listingId }, { fetchPolicy: 'no-cache' })
                .subscribe(result => {
                    if (!result.loading) {
                        this.listingLoaded = true
                        this.listing = result.data.listing as Listing
                        this._title.setTitle(this.listing?.summary.title)
                        this.minNightsStay = this.listing?.stayDurationConditions?.minimum

                        // Only log if we haven't seen this listing+dates combination
                        if (!this.viewTracker.hasLogged(this.listingId, this.travelDates)) {
                            this._amplitude.logEvent('Listing Page Viewed', this.toEventProperties(this.travelDates))
                            this.viewTracker.markLogged(this.listingId, this.travelDates)
                        }
                    }
                })
        }
    }

    private booking$(bookingId: string): Observable<Booking> {
        return this._bookingGQL.watch({ bookingId })
            .valueChanges.pipe(map(result => {
                if (result.errors && result.errors.length > 0) {
                    throw new Error(result.errors[0].message)
                }
                return result.data.booking as Booking
            }))
    }

    private createBooking$ = (selectedTravel: SelectedTravel) => {
        if (!selectedTravel.travelDates.checkin || !selectedTravel.travelDates.checkout) {
            return of(true).pipe(
                map(() => {
                    throw new Error('Cannot create booking without travel dates');
                })
            );
        }

        const checkin = toLocalDate(selectedTravel.travelDates.checkin);
        const checkout = toLocalDate(selectedTravel.travelDates.checkout);
        
        const isWhiteLabelConsumer = this._whiteLabelService.isWhiteLabelEnabled() &&
                                    this._roleManager.hasRole(Role.Consumer);

        const createBooking: CreateBookingRequest = {
            listingId: this.listingId,
            checkin,
            checkout,
            guestCounts: this.guestCounts,
            // Only set onBehalfOf for non-white-label consumer bookings
            onBehalfOf: !isWhiteLabelConsumer ?
                { externalReference: this.formGroup.value.businessCustomerReference } :
                undefined,
            acceptBookingUrl: `${environment.memberUrl}my-guests/{BOOKING_ID}?action=accept`,
            declineBookingUrl: `${environment.memberUrl}my-guests/{BOOKING_ID}?action=decline`,
            // Only set priceModification for non-white-label consumer bookings
            priceModification: !isWhiteLabelConsumer ? {
                adjustedBookingFee: selectedTravel.adjustedPrice
            } : undefined,
            confirmationFlow: this.listing?.allowInstantBook
                ? BookingConfirmationFlow.InstantBook
                : BookingConfirmationFlow.RequestToBook,
            bookingToken: selectedTravel.bookingToken,
        };

        return this._createBookingGQL
            .mutate({ input: createBooking })
            .pipe(
                map(response => {
                    if (response?.errors?.length) {
                        throw new Error(`There was an error preparing the booking: ${response.errors[0].message}`);
                    }

                    if (!response?.data?.createBooking.id) {
                        throw new Error('There was an error: No booking ID');
                    }

                    return response.data.createBooking.id;
                })
            );
    }

    private checkout$ = (booking: Booking) => {
        const url = window.location.href.split('?')[0]
        const bookingCheckout = {
            bookingId: booking.bookingId,
            psp: Psp.Stripe,
            redirects: {
                successUrl: `${environment.memberUrl}my-trips/${booking.tripId}`,
                cancelUrl: url
            }
        } as BookingCheckoutRequest

        return this._checkoutGQL
            .mutate({ input: bookingCheckout })
            .pipe(
                map(response => {
                    if (response?.errors?.length) {
                        const message = `There was an error preparing the booking: ${response.errors[0].message}`
                        throw new Error(message)
                    }

                    if (!response?.data?.checkout?.sessionId) {
                        const message = 'There was an error: No session ID'
                        throw new Error(message)
                    }

                    return response.data.checkout.sessionId
                })
            )
    }

    private stripeCheckout$ = (checkoutSessionId: string) =>
        from(
            loadStripe(environment.stripe.key, { stripeAccount: environment.stripe.connectedAccount })
        ).pipe(
            concatMap(stripe => {
                if (!stripe) {
                    const message = 'There was an error loading Stripe'
                    throw new Error(message)
                }

                return this.redirectToToCheckoutSuccess$(stripe, checkoutSessionId)
            })
        )

    private redirectToToCheckoutSuccess$ = (stripe: Stripe, sessionId: string) =>
        from(stripe.redirectToCheckout({
            sessionId
        })).pipe(
            map(result => {
                if (!result?.error) { return true }
                throw new Error(result.error.message)
            }))

    ngOnDestroy() {
        super.ngOnDestroy();
        this.viewTracker.reset();
    }
}
